import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Projects.css';

const Projects = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: window.innerWidth <= 768 ? 0.1 : 0.3,
        rootMargin: '50px'
      }
    );

    const section = document.getElementById('projects');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const galleryImages = [
    '/Cats/cat1.png',
    '/Cats/cat2.png',
    '/Cats/cat3.png',
    '/Cats/cat4.png',
    '/Cats/cat5.png',
    '/Cats/sword.png'
  ];

  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <div className={`projects-content ${isVisible ? 'visible' : ''}`}>

          <div className="mythbound-project fade-in-up">
            <div className="project-header">
              <div className="project-logo">
                <img src="/Cats/Gamelogo.jpg" alt="Mythbound Game Logo" className="game-logo" />
              </div>
              <h3 className="project-title">
                <span className="mythbound-text">Mythbound</span>
              </h3>
              <p className="project-subtitle">{t('projects.subtitle')}</p>
            </div>

            <div className="project-description">
              <div className="section-header">
                <span className="header-icon">🐟</span>
                <h4 className="neon-header">{t('projects.mythboundsNextGen')}</h4>
              </div>
              <p>
                {t('projects.mythboundsDescription1')}
              </p>

              <div className="section-divider">
                <span className="divider-line"></span>
              </div>

              <div className="section-header">
                <span className="header-icon">🐟</span>
                <h4 className="neon-header">{t('projects.mythboundsScope')}</h4>
              </div>
              <p>
                {t('projects.mythboundsDescription2')}
              </p>

              <div className="section-divider">
                <span className="divider-line"></span>
              </div>

              <div className="section-header">
                <span className="header-icon">🐟</span>
                <h4 className="neon-header">{t('projects.mythboundsArt')}</h4>
              </div>
              <p>
                {t('projects.mythboundsDescription3')}
              </p>

              <div className="section-divider">
                <span className="divider-line"></span>
              </div>

              <div className="section-header">
                <span className="header-icon">🐟</span>
                <h4 className="neon-header">{t('projects.mythboundsWorlds')}</h4>
              </div>
              <p>
                {t('projects.mythboundsDescription4')}
              </p>

              <div className="section-divider">
                <span className="divider-line"></span>
              </div>

              <div className="section-header">
                <span className="header-icon">🐟</span>
                <h4 className="neon-header">{t('projects.mythboundsCoop')}</h4>
              </div>
              <p>
                {t('projects.mythboundsDescription5')}
              </p>

              <div className="section-divider">
                <span className="divider-line"></span>
              </div>

              <div className="inspiration-section">
                <span className="neon-text">{t('projects.inspiredBy')}</span>
              </div>
            </div>

            <div className="project-features">
              <div className="feature-item">
                <span className="feature-icon">⚔️</span>
                <span className="feature-text">{t('projects.heavyAgilityCombat')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🧙‍♂️</span>
                <span className="feature-text">{t('projects.proceduralWorlds')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎨</span>
                <span className="feature-text">{t('projects.purrianArtStyle')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎮</span>
                <span className="feature-text">{t('projects.playerCoopSandbox')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📖</span>
                <span className="feature-text">{t('projects.dualProgressionSystem')}</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🐾</span>
                <span className="feature-text">{t('projects.impactfulLootArtifacts')}</span>
              </div>
            </div>

            <div className="character-showcase">
              <div className="character-grid">
                <img src="/Cats/cat1.png" alt="Purrian Character 1" className="character-img" onClick={() => openGallery(0)} />
                <img src="/Cats/cat2.png" alt="Purrian Character 2" className="character-img" onClick={() => openGallery(1)} />
                <img src="/Cats/cat3.png" alt="Purrian Character 3" className="character-img" onClick={() => openGallery(2)} />
                <img src="/Cats/cat4.png" alt="Purrian Character 4" className="character-img" onClick={() => openGallery(3)} />
                <img src="/Cats/cat5.png" alt="Purrian Character 5" className="character-img" onClick={() => openGallery(4)} />
              </div>
              <div className="weapon-showcase">
                <img src="/Cats/sword.png" alt="Mythbound Weapon Example" className="weapon-img" onClick={() => openGallery(5)} />
              </div>
            </div>

            <button
              className="btn mythbound-btn"
              onClick={scrollToContact}
            >
              {t('projects.learnMore')}
            </button>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {isGalleryOpen && (
        <div className="gallery-modal" onClick={closeGallery}>
          <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-close" onClick={closeGallery}>×</button>
            <button className="gallery-nav gallery-prev" onClick={prevImage}>‹</button>
            <div className="gallery-image-container">
              <img
                src={galleryImages[currentImageIndex]}
                alt={`Mythbound artwork ${currentImageIndex + 1}`}
                className="gallery-image"
              />
            </div>
            <button className="gallery-nav gallery-next" onClick={nextImage}>›</button>
            <div className="gallery-indicators">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  className={`gallery-indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;